import {
  ImportDuplicateStrategy,
  ImportTagStrategy,
  type ImportColumn,
  type ImportEntity,
  type ImportField,
  type ImportFieldMappingInput,
  type ImportPreview
} from '@paintscout/api'
import { uuid } from '@paintscout/util/builder'
import { useMemo, useReducer } from 'react'
import { getCombinedFieldColumns } from './combinedFieldHint'

export const IMPORT_STEPS = ['Upload', 'Map fields', 'Review', 'Import'] as const
export type ImportStepIndex = 0 | 1 | 2 | 3

export interface ImportWizardFile {
  name: string
  size: number
}

export interface ImportColumnsResult {
  fileKey: string
  rowCount: number
  matchedCount: number
  columns: ImportColumn[]
  fields: ImportField[]
}

export interface ImportWizardState {
  step: ImportStepIndex
  entity: ImportEntity
  file?: ImportWizardFile
  fileKey?: string
  rowCount?: number
  matchedCount?: number
  columns: ImportColumn[]
  fields: ImportField[]
  /** columnIndex -> field key, or null for "Don't import". */
  mapping: Record<number, string | null>
  duplicateStrategy: ImportDuplicateStrategy
  tagStrategy: ImportTagStrategy
  clientRequestId?: string
  preview?: ImportPreview
  /**
   * The last preview loaded before a duplicate-strategy change, kept until the new one loads. Review
   * draws its layout from it so a reload does not flash a different screen (#24977). Cleared
   * whenever the plan's inputs change in any other way.
   */
  previousPreview?: ImportPreview
  /**
   * The user who will own the created records, without the `auth0|` prefix. Support and superadmin
   * users must choose one (`startImport` refuses them without it); nobody else sees the picker.
   */
  ownerId?: string
  jobId?: string
}

export type ImportWizardAction =
  | { type: 'FILE_PARSED'; file: ImportWizardFile; result: ImportColumnsResult }
  | { type: 'SET_MAPPING'; columnIndex: number; field: string | null }
  | { type: 'ENTER_REVIEW' }
  | { type: 'SET_DUPLICATE_STRATEGY'; duplicateStrategy: ImportDuplicateStrategy }
  | { type: 'SET_TAG_STRATEGY'; tagStrategy: ImportTagStrategy }
  | { type: 'PREVIEW_LOADED'; preview: ImportPreview }
  | { type: 'SET_OWNER'; ownerId?: string }
  | { type: 'ENTER_IMPORT' }
  | { type: 'JOB_STARTED'; jobId: string }
  | { type: 'BACK' }
  | { type: 'RESET' }

export const TAGS_FIELD_KEY = 'tags'
export const COMPANY_FIELD_KEY = 'company'

export function getInitialState(entity: ImportEntity): ImportWizardState {
  return {
    step: 0,
    entity,
    columns: [],
    fields: [],
    mapping: {},
    duplicateStrategy: ImportDuplicateStrategy.FillEmpty,
    tagStrategy: ImportTagStrategy.Add
  }
}

/**
 * Seeds the mapping from the server's `suggestedField`, keeping it one-to-one: if two columns
 * suggest the same field, the first column wins and the later ones start unmapped.
 */
function initialMapping(columns: ImportColumn[], fields: ImportField[]): Record<number, string | null> {
  const available = new Set(fields.map((field) => field.key))
  const used = new Set<string>()
  const mapping: Record<number, string | null> = {}

  for (const column of columns) {
    const suggested = column.suggestedField
    if (suggested && available.has(suggested) && !used.has(suggested)) {
      mapping[column.index] = suggested
      used.add(suggested)
    } else {
      mapping[column.index] = null
    }
  }

  return mapping
}

export function importWizardReducer(state: ImportWizardState, action: ImportWizardAction): ImportWizardState {
  switch (action.type) {
    case 'FILE_PARSED':
      return {
        ...state,
        step: 1,
        file: action.file,
        fileKey: action.result.fileKey,
        rowCount: action.result.rowCount,
        matchedCount: action.result.matchedCount,
        columns: action.result.columns,
        fields: action.result.fields,
        mapping: initialMapping(action.result.columns, action.result.fields),
        preview: undefined,
        previousPreview: undefined,
        jobId: undefined,
        clientRequestId: undefined
      }

    case 'SET_MAPPING': {
      const mapping = { ...state.mapping, [action.columnIndex]: action.field }

      // Keep the mapping one-to-one — the server rejects a duplicate field as MAPPING_INVALID.
      if (action.field) {
        for (const key of Object.keys(mapping)) {
          const index = Number(key)
          if (index !== action.columnIndex && mapping[index] === action.field) {
            mapping[index] = null
          }
        }
      }

      return { ...state, mapping, preview: undefined, previousPreview: undefined }
    }

    case 'ENTER_REVIEW':
      // A new plan needs a new request id; startImport derives the job id from it.
      return { ...state, step: 2, clientRequestId: uuid(), preview: undefined, previousPreview: undefined }

    case 'SET_DUPLICATE_STRATEGY':
      if (action.duplicateStrategy === state.duplicateStrategy) {
        return state
      }
      return {
        ...state,
        duplicateStrategy: action.duplicateStrategy,
        clientRequestId: uuid(),
        preview: undefined,
        previousPreview: state.preview ?? state.previousPreview
      }

    case 'SET_TAG_STRATEGY':
      // planImport does not use tagStrategy for its counts, so no re-plan and no new id.
      return { ...state, tagStrategy: action.tagStrategy }

    case 'SET_OWNER':
      if (action.ownerId === state.ownerId) {
        return state
      }
      // A different owner is a different job: a new id, so a retry can never return a job that was
      // started for the old owner. The counts do not depend on the owner, so no new preview.
      return { ...state, ownerId: action.ownerId, clientRequestId: state.clientRequestId ? uuid() : undefined }

    case 'PREVIEW_LOADED':
      return { ...state, preview: action.preview, previousPreview: undefined }

    case 'ENTER_IMPORT':
      return { ...state, step: 3 }

    case 'JOB_STARTED':
      return { ...state, jobId: action.jobId }

    case 'BACK': {
      if (state.step === 0) {
        return state
      }
      const step = (state.step - 1) as ImportStepIndex
      // Leaving Review discards the plan; re-entering mints a fresh id and re-runs the preview.
      return { ...state, step, preview: undefined, previousPreview: undefined }
    }

    case 'RESET':
      // "Import another file" is usually for the same customer, so the chosen owner stays.
      return { ...getInitialState(state.entity), ownerId: state.ownerId }

    default:
      return state
  }
}

export interface ImportWizardSelectors {
  fieldMapping: ImportFieldMappingInput[]
  missingRequiredFields: ImportField[]
  usedFieldKeys: Set<string>
  tagsMapped: boolean
  companyMapped: boolean
  inlineEligible: boolean
  combinedFieldColumns: number[]
}

export function getSelectors(state: ImportWizardState): ImportWizardSelectors {
  const fieldMapping: ImportFieldMappingInput[] = state.columns.map((column) => ({
    columnIndex: column.index,
    field: state.mapping[column.index] ?? null
  }))

  const usedFieldKeys = new Set(
    Object.values(state.mapping).filter((field): field is string => typeof field === 'string' && field.length > 0)
  )

  const missingRequiredFields = state.fields.filter((field) => field.required && !usedFieldKeys.has(field.key))

  return {
    fieldMapping,
    missingRequiredFields,
    usedFieldKeys,
    tagsMapped: usedFieldKeys.has(TAGS_FIELD_KEY),
    companyMapped: usedFieldKeys.has(COMPANY_FIELD_KEY),
    inlineEligible: state.preview?.inlineEligible ?? false,
    combinedFieldColumns: getCombinedFieldColumns(state.columns, state.mapping)
  }
}

export function useImportWizard(entity: ImportEntity) {
  const [state, dispatch] = useReducer(importWizardReducer, entity, getInitialState)
  const selectors = useMemo(() => getSelectors(state), [state])

  return { state, dispatch, ...selectors }
}
